import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { authApi, setAuthFailureHandler } from '@/api/client'
import {
  clearTokens,
  getRefreshToken,
  setAccessToken,
  setDisplayName,
  setRefreshToken,
  setRememberMe,
} from '@/auth/tokenStore'
import type { AuthMeResponse } from '@/types/api'

export interface AuthUser {
  userId: string
  username: string
  roles: string[]
}

interface AuthContextValue {
  user: AuthUser | null
  bootstrapping: boolean
  login: (username: string, password: string, rememberMe?: boolean) => Promise<AuthUser>
  logout: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

function userFromMe(me: AuthMeResponse): AuthUser {
  return {
    userId: me.userId,
    username: me.userName,
    roles: me.roles ?? [],
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [bootstrapping, setBootstrapping] = useState(true)

  const clearSession = useCallback(() => {
    clearTokens()
    setUser(null)
  }, [])

  const logout = useCallback(async () => {
    const refresh = getRefreshToken()
    clearSession()
    if (refresh) {
      try {
        await authApi.logout(refresh)
      } catch {
        // ignore logout errors
      }
    }
  }, [clearSession])

  useEffect(() => {
    setAuthFailureHandler(() => {
      clearSession()
    })
    return () => setAuthFailureHandler(null)
  }, [clearSession])

  useEffect(() => {
    let cancelled = false
    async function restore() {
      const refresh = getRefreshToken()
      if (!refresh) {
        if (!cancelled) setBootstrapping(false)
        return
      }
      try {
        const data = await authApi.refresh(refresh)
        if (cancelled) return
        setAccessToken(data.token)
        setRefreshToken(data.refreshToken)
        const me = await authApi.me()
        if (cancelled) return
        const next = userFromMe(me)
        setDisplayName(next.username)
        setUser(next)
      } catch {
        clearTokens()
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setBootstrapping(false)
      }
    }
    void restore()
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (username: string, password: string, rememberMe = false) => {
    setRememberMe(rememberMe)
    try {
      const data = await authApi.login(username, password)
      setAccessToken(data.token)
      setRefreshToken(data.refreshToken)
      setDisplayName(username)
      const me = await authApi.me()
      const next = userFromMe(me)
      setDisplayName(next.username)
      setUser(next)
      return next
    } catch (err) {
      clearTokens()
      setUser(null)
      throw err
    }
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      bootstrapping,
      login,
      logout,
      isAuthenticated: !!user,
    }),
    [user, bootstrapping, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
